import { Inject, Injectable, OpaqueToken } from "@angular/core";
import { JwtHttpClientService } from "../http/jwt-http-client.service";
import { AbstractHttpService } from "../http/abstract-http.service";
import { TranslateService } from "ng2-translate";
import { ResourceModule } from "../resource/resource.module";
export class ValidationService extends AbstractHttpService {
    /**
     *
     * @param http HTTP Service
     * @param errorService Error Service
     * @param translate Translate service used for field name translation
     * @param application Application key used for field name lookup
     */
    constructor(http, translate, application, nameSpace) {
        super();
        this.http = http;
        this.translate = translate;
        this.application = application;
        this.nameSpace = nameSpace;
    }
    /**
     * Validate the entity data and all of its fields
     * @param entityData
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    validate(entityData) {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then(validation => validation.validate())
                .then(validationMessages => {
                if (!validationMessages || validationMessages.length == 0) {
                    resolve(null);
                }
                else {
                    resolve(validationMessages);
                }
            })
                .catch(error => console.log("Failed to perform validation for " + entityData.constructor.name + ": " + error));
        });
    }
    /**
     * Validate the entity data (no field validation)
     * @param entityData
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    validateEntity(entityData) {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then(validation => validation.validateEntity())
                .then(validationMessages => {
                if (!validationMessages || validationMessages.length == 0) {
                    resolve(null);
                }
                else {
                    resolve(validationMessages);
                }
            })
                .catch(error => console.log("Failed to perform validation for " + entityData.constructor.name + ": " + error));
        });
    }
    /**
     * Validate a child entity data and all of its fields
     * @param {EntityData<any>} entityData
     * @param {EntityData<any>} childEntityData
     * @param {string} fieldPath
     * @param {number} rowIndex
     * @returns {Promise<ValidationMessage[]>}
     */
    validateChild(entityData, childEntityData, fieldPath, rowIndex) {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then(validation => validation.validateChild(childEntityData, fieldPath, rowIndex))
                .then(validationMessages => {
                if (!validationMessages || validationMessages.length == 0) {
                    resolve(null);
                }
                else {
                    resolve(validationMessages);
                }
            })
                .catch(error => console.log("Failed to perform validation for " + entityData.constructor.name + ": " + error));
        });
    }
    /**
     * Returns a Promise for validation of the specified field in the specified entity data.
     * @param entityData
     * @param fieldValue
     * @param fieldPath
     * @param rowIndex
     * @param fieldIdentifier
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    validateField(entityData, fieldValue, fieldPath, rowIndex, fieldIdentifier) {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .then((validation) => validation.validateField(fieldPath, fieldValue, undefined, rowIndex, fieldIdentifier))
                .then((validationMessages) => {
                if (!validationMessages || validationMessages.length == 0) {
                    resolve(null);
                }
                else {
                    resolve({ validationMessages: validationMessages });
                }
            })
                .catch(error => console.log("Failed to perform validation for " + entityData.constructor.name + "." + fieldPath + ": " + error));
        });
    }
    isFieldRequired(entityData, fieldPath) {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .catch(error => console.log("Failed to get field required for " + entityData.constructor.name + "." + fieldPath + ": " + error))
                .then((validation) => resolve(validation.isFieldRequired(fieldPath)));
        });
    }
    hasAnyValidation(entityData, fieldPath) {
        return new Promise(resolve => {
            entityData.getValidation(this)
                .catch(error => console.log("Failed to get hasAnyValidation for " + entityData.constructor.name + "." + fieldPath + ": " + error))
                .then((validation) => resolve(validation.hasAnyValidation(fieldPath)));
        });
    }
    /**
     * Performs server-side validation of a field
     * @param entityData
     * @param fieldPath
     * @param fieldValue
     * @param rowIndex
     * @param validatorName
     * @param fieldIdentifier
     * @returns {Promise<T>} a promise of a list of validation messages
     */
    validateFieldOnServer(entityData, fieldPath, rowIndex, fieldIdentifier, fieldValue, validatorName) {
        if (rowIndex === undefined) {
            rowIndex = null;
        }
        var body = `{
            "identifier": ${entityData.getIdentifier() === undefined ? null : JSON.stringify(entityData.getIdentifier())},
            "rowIndex": ${rowIndex},
            "fieldValue": ${fieldValue === undefined ? null : JSON.stringify(fieldValue)},
            "fieldIdentifier": ${fieldIdentifier === undefined ? null : JSON.stringify(fieldIdentifier)},
            "entityData": ${entityData.toJSONString()}
        }`;
        return this.http.post(ValidationService.URL_PREFIX + this.nameSpace + "/" + entityData.constructor.name + "/" + fieldPath + "/" + validatorName, body)
            .map((response) => response.json() || {})
            .toPromise();
    }
    /**
     * Returns an observable for the validation script text.
     * Multiple concurrent calls to this method will return the same observable, so that the script will not be fetched multiple times.
     * @returns {any}
     */
    getValidationScript() {
        // Only allow a single concurrent request
        if (this.scriptObservable) {
            return this.scriptObservable;
        }
        return this.scriptObservable = this.http.get(ValidationService.URL_PREFIX)
            .catch((response) => this.handleServiceError(response))
            .map((response) => response.text())
            .finally(() => this.scriptObservable = undefined)
            .share();
    }
    /**
     * Returns a promise of a ValidationFactory.
     * The ValidationFactory is obtained by invoking the function parsed from the validation script fetched from the server.
     * Multiple concurrent calls to this method will return the same promise, so that the script will not be parsed multiple times.
     * Once the promise has been resolved, subsequent calls to this method will return a new promise and re-parse the script.
     * However, the ValidationFactory itself is cached and re-used by createValidation() below, rather than re-calling this method.
     * @returns {Promise<ValidationFactory>}
     */
    loadValidationFactory() {
        // Only allow a single concurrent load
        if (this.factoryPromise) {
            return this.factoryPromise;
        }
        return this.factoryPromise = new Promise((resolve, reject) => {
            this.getValidationScript().subscribe((script) => {
                try {
                    this.validationFunction = new Function(script);
                    resolve(this.validationFactory = this.validationFunction());
                    this.factoryPromise = undefined;
                }
                catch (error) {
                    reject("Failed to parse validation script: " + error);
                }
            });
        });
    }
    /**
     * Returns a promise of a Validation for the specified entity data.
     * If the validation factory function has already been loaded, then it will be immediately invoked to create the Validation,
     * and a pre-resolved Promise of that validation will be returned.
     * Otherwise, the returned Promise will be resolved once the validation factory function has been asynchronously loaded and invoked to create the Validation.
     * Each call to this method will invoke the validation factory function, which means executing the entire validation script to build a separate copy of its entities array.
     * For this reason, direct calls to this method should be avoided in favour of EntityData.getValidation() which caches a single validation for the entity data instance.
     * @param entityData
     * @returns {Promise<Validation>}
     */
    createValidation(entityData) {
        if (this.validationFactory) {
            return Promise.resolve(this.validationFactory.createValidation(this, this.nameSpace, entityData.constructor.name, entityData, entityData.getIdentifier()));
        }
        return new Promise((resolve, reject) => {
            this.loadValidationFactory()
                .then(validationFactory => {
                return resolve(validationFactory.createValidation(this, this.nameSpace, entityData.constructor.name, entityData, entityData.getIdentifier()));
            })
                .catch(error => reject(error));
        });
    }
}
/**
 * Static URL prefix for all server side calls
 * @type {string}
 */
ValidationService.URL_PREFIX = '/dataserver/validation/';
/**
 * Provider application key that is used during dependency injection and is used to identify the application in the translation file.
 * @type {OpaqueToken}
 */
ValidationService.APPLICATION_KEY = new OpaqueToken('applicationKey');
ValidationService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
ValidationService.ctorParameters = () => [
    { type: JwtHttpClientService, },
    { type: TranslateService, },
    { type: undefined, decorators: [{ type: Inject, args: [ValidationService.APPLICATION_KEY,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [ResourceModule.RESOURCE_NAMESPACE_KEY,] },] },
];
//# sourceMappingURL=validation.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/validation/validation.service.js
// module id = 219
// module chunks = 1